import { Column, SectionHeader } from '../common/SharedComponents';
import { useEffect, useState } from 'react';
import { MonthVsMonthReportEntry, PaymentApi, PaymentFailures, TopCustomers } from '@apply/api/src/api/PaymentApi';
import { applicationInsights } from '../ApplicationInsights';
import PieChart, { Label, Connector } from 'devextreme-react/pie-chart';
import { paymentStatusLabels } from '@apply/constants';
import { registerGradient } from 'devextreme/common/charts';
import { DataGrid } from 'devextreme-react';
import { Chart, Series, ArgumentAxis, ValueAxis, Tooltip, Legend, Title } from 'devextreme-react/chart';

export const PaymentDashboard: React.FC = () => {
    interface CountByPaymentTypeReportRow {
        status: string;
        count: number;
    }

    const [countByPaymentTypeReport, set_countByPaymentTypeReport] = useState<CountByPaymentTypeReportRow[]>([
        { status: paymentStatusLabels[1], count: 0 },
        { status: paymentStatusLabels[2], count: 0 },
        { status: paymentStatusLabels[3], count: 0 },
        { status: paymentStatusLabels[4], count: 0 },
        { status: paymentStatusLabels[5], count: 0 },
        { status: paymentStatusLabels[6], count: 0 },
    ]);
    const [topCustomersReport, set_topCustomersReport] = useState<TopCustomers[]>([]);
    const [failuresReport, set_failures] = useState<PaymentFailures[]>([]);
    const [monthReportData, set_monthReportData] = useState<MonthVsMonthReportEntry[]>([]);
    const [monthVsMonthStart, set_MonthVsMonthStart] = useState('');
    const [monthVsMonthEnd, set_MonthVsMonthEnd] = useState('');

    const paymentApi = new PaymentApi(applicationInsights);

    const loadMonthVsMonthReport = async () => {
        const response = await paymentApi.report_MonthVsMonthPayments();
        const { reportData, monthA, monthB } = response.result;

        // Format data for the bar chart
        const formattedData: MonthVsMonthReportEntry[] = reportData.map((day) => ({
            monthDate: day.monthDate,
            totalA: day.totalA,
            totalB: day.totalB,
        }));

        set_MonthVsMonthStart(monthA);
        set_MonthVsMonthEnd(monthB);
        set_monthReportData(formattedData);
    };

    const loadCountByPaymentTypeReport = async () => {
        const dayWindow = 30;

        const response = await paymentApi.report_CountByPaymentType(dayWindow);

        const updatedReport = [...countByPaymentTypeReport];
        response.result.forEach((row) => {
            updatedReport[row.paymentStatus - 1].count = row.count;
        });

        set_countByPaymentTypeReport(updatedReport);
    };

    const loadTopCustomersReport = async () => {
        const dayWindow = 30;

        const response = await paymentApi.report_TopCustomers(dayWindow);

        set_topCustomersReport(response.result);
    };

    const loadFailures = async () => {
        const dayWindow = 7;

        const response = await paymentApi.report_PaymentFailures(dayWindow);

        set_failures(response.result);
    };

    useEffect(() => {
        loadCountByPaymentTypeReport();
        loadTopCustomersReport();
        loadFailures();
        loadMonthVsMonthReport();
    }, []);

    function getGradient(type: string, color1: string, color2: string) {
        return registerGradient(type, {
            colors: [
                {
                    offset: '1%',
                    color: color1,
                },
                {
                    offset: '50%',
                    color: color2,
                },
            ],
        });
    }

    function hexToRgb(hex: string, opacity = 1) {
        const hexColorParts = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!hexColorParts) return;

        return `rgba(${parseInt(hexColorParts[1], 16)}, ${parseInt(hexColorParts[2], 16)}, ${parseInt(hexColorParts[3], 16)}, ${opacity})`;
    }

    const customColors = [
        getGradient('linear', '#eeeeee', hexToRgb('#777777', 0.9) as string),
        getGradient('linear', '#ffdddd', hexToRgb('#aa5555', 0.9) as string),
        getGradient('linear', '#ddffdd', hexToRgb('#55aa55', 0.9) as string),
        getGradient('linear', '#ddddff', hexToRgb('#5555aa', 0.9) as string),
        getGradient('linear', '#ddf0ff', hexToRgb('#5599aa', 0.9) as string),
        getGradient('linear', '#ffddff', hexToRgb('#9955aa', 0.9) as string),
    ];

    return (
        <>
            <SectionHeader title='Payment Dashboard' />
            <div
                className='main-content'
                style={{ padding: 0, backgroundColor: '#f4f4f4', height: '100%' }}
            >
                <div
                    className='scrollable-content'
                    style={{ display: 'grid' }}
                >
                    <div className='quadrant pie-chart-section'>
                        <PieChart
                            id='countByPaymentTypeReport'
                            type='doughnut'
                            title='Payments by Status'
                            palette={customColors}
                            dataSource={countByPaymentTypeReport}
                            className='payment-dashboard-status-pie-chart'
                        >
                            <Series
                                argumentField='status'
                                valueField='count'
                            >
                                <Label visible={true}>
                                    <Connector visible={true} />
                                </Label>
                            </Series>
                            <Legend
                                margin={0}
                                horizontalAlignment='right'
                                verticalAlignment='top'
                            />
                        </PieChart>
                    </div>
                    <div className='quadrant top-customers-grid'>
                        <span className='payment-dashboard-header'>Top Customers</span>
                        <DataGrid
                            dataSource={topCustomersReport}
                            showColumnHeaders={false}
                            showBorders={false}
                            columnAutoWidth={false}
                            rowAlternationEnabled={true}
                        >
                            <Column
                                dataField='name'
                                caption='Name'
                            />
                            <Column
                                dataField='email'
                                caption='Email'
                            />
                            <Column
                                dataField='total'
                                caption='Amount'
                                format={{ type: 'currency', precision: 2 }}
                            />
                        </DataGrid>
                    </div>
                    <div className='quadrant failures-grid'>
                        <span className='payment-dashboard-header'>List of Failures</span>
                        <DataGrid
                            dataSource={failuresReport}
                            showColumnHeaders={false}
                            showBorders={false}
                            columnAutoWidth={false}
                            rowAlternationEnabled={true}
                        >
                            <Column
                                dataField='name'
                                caption='Name'
                            />
                            <Column
                                dataField='phone'
                                caption='Phone'
                            />
                            <Column
                                dataField='email'
                                caption='Email'
                            />
                            <Column
                                dataField='amount'
                                caption='Amount'
                                format={{ type: 'currency', precision: 2 }}
                            />
                            <Column
                                dataField='note'
                                caption='Message'
                            />
                        </DataGrid>
                    </div>
                    <div className='quadrant month-month'>
                        <Chart dataSource={monthReportData}>
                            <Title text='Month vs Month Comparison' />
                            <ArgumentAxis label={{ overlappingBehavior: 'rotate', rotationAngle: 50 }}>
                                <Title text='Dates' />
                            </ArgumentAxis>
                            <ValueAxis>
                                <Title text='Gross Volume' />
                            </ValueAxis>
                            <Series
                                type='bar'
                                argumentField='monthDate'
                                valueField='totalA'
                                name={monthVsMonthStart}
                                color='#5A9BD5'
                            />
                            <Series
                                type='bar'
                                argumentField='monthDate'
                                valueField='totalB'
                                name={monthVsMonthEnd}
                                color='#ED7D31'
                            />
                            <Legend
                                verticalAlignment='bottom'
                                horizontalAlignment='center'
                            />
                            <Tooltip 
                            enabled={true} 
                            customizeTooltip={(info) => {
                                return {
                                  text: `Date: ${info.argumentText}\nVolume: ${info.valueText}`,
                                };
                              }}/>
                        </Chart>
                    </div>
                </div>
            </div>
        </>
    );
};
